import React from "react";
import cs from "classnames";

import { tooltips } from "../../../../../lib/utils/tooltips";
import { useConfirmation } from "../../../../../lib/context/Confirmation/ConfirmationContext";

import { Button } from "../../../Button/Button";
import { Switch } from "../../../Switch/Switch";
import { EditDeviceFormik } from "../lib/useEditDeviceFormik";
import { SectionHeader } from "../ui/SectionHeader";
import { Notice } from "../ui/Notice";

interface Props {
  formik: EditDeviceFormik;
  // null when the device is unassigned or has no data yet
  appName: string | null;
  appVersion: string;
  shouldUpdate: boolean;
  isUpdating: boolean;
  // Without push the update can't reach the device
  pushAvailable: boolean;
  onUpdate: () => void;
}

const getStatus = (isUpdating: boolean, shouldUpdate: boolean) => {
  if (isUpdating) {
    return { tone: "progress", text: "Update in progress…" };
  }

  return shouldUpdate
    ? { tone: "newer", text: "A newer version is available" }
    : { tone: "latest", text: "Running the latest version" };
};

export const SoftwareSection = ({
  formik,
  appName,
  appVersion,
  shouldUpdate,
  isUpdating,
  pushAvailable,
  onUpdate,
}: Props) => {
  const status = getStatus(isUpdating, shouldUpdate);
  const { confirm } = useConfirmation();

  const handleUpdate = async () => {
    try {
      await confirm({
        title: `Update ${appName}?`,
        description: "The update runs immediately and may restart the display.",
      });
    } catch {
      // Cancelled
      return;
    }

    onUpdate();
  };

  return (
    <section className="DeviceSection SoftwareSection">
      <SectionHeader icon="layers-icon" title="Software" />

      {appName === null ? (
        <Notice tone="muted">Unassigned or no data found.</Notice>
      ) : (
        <>
          <p className="SoftwareSection__app">{appName}</p>

          <div className="SoftwareSection__version">
            <div>
              <p className="SoftwareSection__versionText">
                App version: {appVersion}
              </p>
              <p
                className={cs(
                  "SoftwareSection__status",
                  `SoftwareSection__status--${status.tone}`
                )}
              >
                {status.text}
              </p>
            </div>

            <Button
              type="button"
              title={isUpdating ? "Updating…" : "Update"}
              className="Button--outlined Button--muted"
              disabled={!shouldUpdate || isUpdating || !pushAvailable}
              onClick={handleUpdate}
            />
          </div>

          <Switch
            name="autoUpdate"
            title="Update automatically"
            direction="left"
            message={tooltips.devices.autoUpdate}
            value={formik.values.autoUpdate}
            onChange={(value) => {
              formik.setFieldValue("autoUpdate", value);
              formik.setFieldTouched("autoUpdate");
            }}
          />
        </>
      )}
    </section>
  );
};
